import { useState, type FormEvent } from 'react';
import { authApi, ApiError } from '../api';
import { useTheme } from '../themes/ThemeContext';

export function SignupPage() {
  const theme = useTheme();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError('');

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (password.length < 10) {
      setError('Password must be at least 10 characters');
      return;
    }

    setLoading(true);

    try {
      await authApi.signup(email, password);
      setSuccess(true);
    } catch (err) {
      if (err instanceof ApiError) {
        if (err.code === 'email_already_exists') {
          setError('An account with this email already exists');
        } else if (err.code === 'password_breached') {
          setError('This password has been found in a data breach. Please choose a different one.');
        } else {
          setError(err.message);
        }
      } else {
        setError('An unexpected error occurred. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    // If the signup was initiated from an OIDC interaction (e.g. the user
    // clicked "Create an account" on the hosted login page), the interaction
    // uid is forwarded so we can resume the flow after signup.
    const interactionUid = new URLSearchParams(window.location.search).get('interaction');
    const returnTo = new URLSearchParams(window.location.search).get('return_to');

    return (
      <div style={{ background: theme.colors.background, color: theme.colors.text, minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ textAlign: 'center', maxWidth: 440, padding: 32 }}>
          <img src={theme.logoUrl} alt={theme.displayName} style={{ height: 48, marginBottom: 24 }} />
          <h1 style={{ fontSize: 24, fontWeight: 600, marginBottom: 16 }}>Account created</h1>

          <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8, padding: 20, marginBottom: 24, textAlign: 'left' }}>
            <p style={{ marginBottom: 12 }}>
              A verification email has been sent to <strong>{email}</strong>.
            </p>
            <p style={{ fontSize: 14, color: theme.colors.textMuted }}>
              Open your inbox and click the verification link to activate your account.
            </p>
          </div>

          <div style={{ background: '#f0f9ff', border: '1px solid #bae6fd', borderRadius: 8, padding: 16, marginBottom: 24, fontSize: 14, color: '#0c4a6e', textAlign: 'left' }}>
            <strong style={{ display: 'block', marginBottom: 4 }}>Next step</strong>
            {interactionUid
              ? 'Once your email is verified, click the button below to sign in and continue.'
              : 'Return to the application that brought you here and sign in with your new account.'}
          </div>

          {interactionUid ? (
            <a
              href={`/oidc/interaction/${interactionUid}`}
              style={{ display: 'inline-block', padding: '10px 20px', background: theme.colors.primary, color: '#fff', borderRadius: 6, textDecoration: 'none', fontWeight: 600, marginBottom: 12 }}
            >
              Sign in to continue
            </a>
          ) : returnTo ? (
            <a
              href={returnTo}
              style={{ display: 'inline-block', padding: '10px 20px', background: theme.colors.primary, color: '#fff', borderRadius: 6, textDecoration: 'none', fontWeight: 600, marginBottom: 12 }}
            >
              Return to application
            </a>
          ) : null}

          {!interactionUid && (
            <div>
              <a href={`/login${window.location.search}`} style={{ color: theme.colors.primary, fontSize: 14 }}>
                Or sign in here
              </a>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div style={{ background: theme.colors.background, color: theme.colors.text, minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ width: '100%', maxWidth: 400, padding: 32 }}>
        <div style={{ textAlign: 'center', marginBottom: 32 }}>
          <img src={theme.logoUrl} alt={theme.displayName} style={{ height: 48, marginBottom: 16 }} />
          <h1 style={{ fontSize: 24, fontWeight: 600 }}>{theme.copy.signupTitle}</h1>
        </div>

        <form onSubmit={handleSubmit}>
          <div style={{ marginBottom: 16 }}>
            <label htmlFor="email" style={{ display: 'block', marginBottom: 4, fontSize: 14 }}>Email</label>
            <input
              id="email"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              style={{ width: '100%', padding: 10, borderRadius: 6, border: '1px solid #d1d5db', fontSize: 16, boxSizing: 'border-box' }}
            />
          </div>

          <div style={{ marginBottom: 16 }}>
            <label htmlFor="password" style={{ display: 'block', marginBottom: 4, fontSize: 14 }}>Password</label>
            <input
              id="password"
              type="password"
              required
              minLength={10}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              style={{ width: '100%', padding: 10, borderRadius: 6, border: '1px solid #d1d5db', fontSize: 16, boxSizing: 'border-box' }}
            />
            <span style={{ fontSize: 12, color: '#6b7280' }}>At least 10 characters</span>
          </div>

          <div style={{ marginBottom: 16 }}>
            <label htmlFor="confirmPassword" style={{ display: 'block', marginBottom: 4, fontSize: 14 }}>Confirm Password</label>
            <input
              id="confirmPassword"
              type="password"
              required
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              style={{ width: '100%', padding: 10, borderRadius: 6, border: '1px solid #d1d5db', fontSize: 16, boxSizing: 'border-box' }}
            />
          </div>

          {error && (
            <div style={{ color: '#dc2626', fontSize: 14, marginBottom: 16 }}>{error}</div>
          )}

          <button
            type="submit"
            disabled={loading}
            style={{ width: '100%', padding: 12, borderRadius: 6, border: 'none', background: theme.colors.primary, color: '#fff', fontSize: 16, fontWeight: 600, cursor: loading ? 'not-allowed' : 'pointer', opacity: loading ? 0.7 : 1 }}
          >
            {loading ? 'Creating account...' : 'Create Account'}
          </button>
        </form>

        <div style={{ textAlign: 'center', marginTop: 16, fontSize: 14 }}>
          <a href={`/login${window.location.search}`} style={{ color: theme.colors.primary }}>Already have an account? Log in</a>
        </div>
      </div>
    </div>
  );
}
